import type { BulkGeneratedBeddingConfigurationResponse } from '~/hooks/apis/offers/useGenerateBeddingConfiguration';
import type { OpportunityRoom as SalesforceRoom } from '~/hooks/apis/offers/useGetOffer';

import { reportError } from '~/utils/reportError';

import { resolveRoomNameMatchesWithAi } from '../llmRoomNameMatcher';
import type { FlashOfferProperty } from '../types';

import { mapGeneratedBeddingConfigurationToBedGroups } from './bedding';
import { fillRoomDescriptions } from './generateRoomDescriptions';
import type { GenerateRoomDescription } from './generateRoomDescriptions';
import { prefillRoomAmenities } from './prefillRoomAmenities';
import type { RoomAmenityLookup, RoomAmenityPrefill } from './prefillRoomAmenities';
import { buildSuggestedAssetRoomKeys } from './roomAssetMapping';
import { getSalesforceRoomsMissingExistingRoomTypes, mapSalesforceRoomsToRoomDrafts } from './utils';
import type { FlashOfferRoomDraft } from './utils';

const BEDDING_WARNING_MESSAGE =
  'Some bedding configurations could not be prefilled. Review room bedding before continuing.';
const DESCRIPTION_WARNING_MESSAGE =
  'Some room descriptions could not be suggested automatically. Fill in or generate them before continuing.';
const AI_MATCHING_FAILED_WARNING_MESSAGE =
  "Salesforce rooms couldn't be matched to the property's existing rooms automatically. Every Salesforce room is proposed as a new room below -- remove any that already exist, and check each deal option's room.";

export interface PrepareRoomDraftsParams {
  existingRoomTypes: Array<App.RoomType>;
  generateBulkBeddingConfigurations: (dealIds: Array<string>) => Promise<BulkGeneratedBeddingConfigurationResponse>;
  generateRoomDescription?: GenerateRoomDescription;
  property: FlashOfferProperty | null;
  roomAmenityLookup?: RoomAmenityLookup;
  salesforceRooms: Array<SalesforceRoom>;
}

export interface PrepareRoomDraftsResult {
  roomAmenityPrefill?: RoomAmenityPrefill;
  roomDrafts: Array<FlashOfferRoomDraft>;
  /** Salesforce room asset id -> the AI's suggested room: an existing room type's id, or the key of a proposed draft room. */
  suggestedAssetRoomKeys: Record<string, string>;
  unmatchedSalesforceRoomNames: Array<string>;
  warningMessage?: string;
}

async function applyBeddingConfigurations(
  rooms: Array<SalesforceRoom>,
  generateBulkBeddingConfigurations: PrepareRoomDraftsParams['generateBulkBeddingConfigurations'],
) {
  const bedGroupsByDealId = new Map<string, SalesforceRoom['bed_groups']>();
  const dealIdByRoomAssetId = new Map<string, string>();
  let hasBeddingConfigurationError = false;

  rooms.forEach((room) => {
    const roomAssetId = room.room_asset?.sfid || room.room_asset?.id;
    const roomAssetKey = roomAssetId ? String(roomAssetId) : '';
    const dealId = room.id ? String(room.id) : '';

    if (roomAssetKey && dealId && !dealIdByRoomAssetId.has(roomAssetKey)) {
      dealIdByRoomAssetId.set(roomAssetKey, dealId);
    }
  });

  const dealIds = Array.from(dealIdByRoomAssetId.values());

  for (let index = 0; index < dealIds.length; index += 10) {
    let response;

    try {
      response = await generateBulkBeddingConfigurations(dealIds.slice(index, index + 10));
    } catch (error) {
      reportError(error);
      hasBeddingConfigurationError = true;
      continue;
    }

    response.result?.forEach((result) => {
      if (result.status !== 200) {
        hasBeddingConfigurationError = true;
        return;
      }

      const bedGroups = mapGeneratedBeddingConfigurationToBedGroups({ result: result.result });

      if (bedGroups.length > 0) {
        bedGroupsByDealId.set(result.deal_offer_id, bedGroups);
      }
    });
  }

  const roomsWithBedGroups = rooms.map((room) => {
    const bedGroups = bedGroupsByDealId.get(String(room.id));
    return bedGroups ? { ...room, bed_groups: bedGroups } : room;
  });

  return {
    rooms: roomsWithBedGroups,
    warningMessage: hasBeddingConfigurationError ? BEDDING_WARNING_MESSAGE : undefined,
  };
}

/**
 * Has the AI match the offer's Salesforce room assets against the property's existing room types,
 * prefills bedding configurations for the ones it couldn't match, and turns those into room
 * drafts -- also suggesting a room for every room asset (its matched existing room, or the
 * draft proposed for it). Pure aside from the injected service calls -- the caller
 * (useRoomsStep) owns applying the result to its own state.
 */
export async function prepareRoomDrafts({
  existingRoomTypes,
  generateBulkBeddingConfigurations,
  generateRoomDescription,
  property,
  roomAmenityLookup,
  salesforceRooms,
}: PrepareRoomDraftsParams): Promise<PrepareRoomDraftsResult> {
  if (!property?.id) {
    return { roomDrafts: [], suggestedAssetRoomKeys: {}, unmatchedSalesforceRoomNames: [] };
  }

  const { hasFailed: hasMatchingFailed, matches: matchedRoomTypeNameBySalesforceName } =
    await resolveRoomNameMatchesWithAi(
      salesforceRooms.map((room) => room.room_asset?.name),
      existingRoomTypes.map((roomType) => roomType.name),
    );
  const suggestedAssetRoomKeys = buildSuggestedAssetRoomKeys({
    existingRoomTypes,
    matchedRoomTypeNameBySalesforceName,
    rooms: salesforceRooms,
  });
  const missingSalesforceRooms = getSalesforceRoomsMissingExistingRoomTypes(
    salesforceRooms,
    matchedRoomTypeNameBySalesforceName,
  );
  // With no AI answer, "unmatched" means "unknown".
  const matchingWarningMessage = hasMatchingFailed ? AI_MATCHING_FAILED_WARNING_MESSAGE : undefined;

  if (missingSalesforceRooms.length === 0) {
    return {
      roomDrafts: [],
      suggestedAssetRoomKeys,
      unmatchedSalesforceRoomNames: [],
      warningMessage: matchingWarningMessage,
    };
  }

  const { rooms, warningMessage: beddingWarningMessage } = await applyBeddingConfigurations(
    missingSalesforceRooms,
    generateBulkBeddingConfigurations,
  );
  const mappedRoomDrafts = mapSalesforceRoomsToRoomDrafts({
    propertyCategory: property.category,
    propertyId: property.id,
    rooms,
  });
  // Before descriptions, so the description generator gets the amenities as context.
  const { prefill: roomAmenityPrefill, roomDrafts: prefilledRoomDrafts } = roomAmenityLookup
    ? await prefillRoomAmenities({
        lookup: roomAmenityLookup,
        reservationPropertyId: property.id,
        roomDrafts: mappedRoomDrafts,
      })
    : { prefill: undefined, roomDrafts: mappedRoomDrafts };
  const { hasFailed: hasDescriptionFailed, roomDrafts } = generateRoomDescription
    ? await fillRoomDescriptions(prefilledRoomDrafts, generateRoomDescription)
    : { hasFailed: false, roomDrafts: prefilledRoomDrafts };

  const warningMessages = [
    matchingWarningMessage,
    beddingWarningMessage,
    hasDescriptionFailed && DESCRIPTION_WARNING_MESSAGE,
  ];

  return {
    roomAmenityPrefill,
    roomDrafts,
    suggestedAssetRoomKeys,
    // Tracked separately so these names stay visible even after the CM deletes every draft.
    unmatchedSalesforceRoomNames: roomDrafts.map((draft) => draft.name).filter(Boolean),
    warningMessage: warningMessages.filter(Boolean).join(' ') || undefined,
  };
}
